import {
  ADS,
  AI_SIGNALS,
  AI_VISIBILITY_REPORT,
  ALL,
  BOTS_ACTIVITY,
  BOTS_ACTIVITY_ANALYSIS,
  BOT_ACCESS,
  CONTEXT_ANALYSIS,
  GEO_CHECKLIST,
  INSIGHTS,
  KEYWORDS,
  PROJECTS,
  PROMPTS,
  PROMPT_ANALYST,
  REPORTS,
  SOURCES,
  SUGGESTED_PROMPTS,
  TAGS,
} from '../../../../routes/path';
import { ProjectResponse } from '../../../../models/projects/ProjectResponse';

export interface ProjectMenuItem {
  /**
   * Prefiks id-ka w DOM (pełne to `${id}-${projectId}`) i cel tooltipa. NIE zmieniać — te same
   * id-ki są w markupie od zawsze i trzymają się ich selektory poza tym plikiem.
   */
  id: string;
  to: string;
  /**
   * Po czym poznajemy, że pozycja należy do bieżącej strony. Osobno od `to`, bo część adresów ma na
   * końcu okres i zakres (`/weekly/all`), które użytkownik zmienia w obrębie tej samej zakładki.
   */
  match: string;
  icon: string;
  /** Klucz i18n. Ten sam idzie do etykiety i do tooltipa, więc muszą zostać jednym kluczem. */
  labelKey: string;
  badge?: 'dev' | 'new';
}

export interface ProjectMenuGroup {
  key: string;
  labelKey: string;
  icon: string;
  items: ProjectMenuItem[];
}

export type ProjectMenuEntry =
  | { kind: 'item'; item: ProjectMenuItem }
  | { kind: 'group'; group: ProjectMenuGroup };

export interface ProjectMenuContext {
  project: ProjectResponse;
  /** Addony ZALOGOWANEGO konta — tak jak dotąd bramkowała je większość pozycji. */
  addons: string[];
  /** Pozycje spod `useModuleAccess` / addonów WŁAŚCICIELA projektu — decyzję podejmuje wołający. */
  showVisibilityReport: boolean;
  isVisibilityReportDevOnly: boolean;
  hasAdsAddon: boolean;
  insights: { canAccess: boolean; isDevOnly: boolean };
  /** Z `promptAnalystAccess` — addon zalogowanego konta, bez triala; bez wyjątku dla superadmina. */
  promptAnalyst: { canAccess: boolean };
  /** Podpięte źródło logów — wtedy „Bots Activity" prowadzi od razu do analizy, bez przebitki. */
  botsActivityHasAnyDataSource: boolean;
  /** Rola `GUEST` — zawęża menu do samego podglądu. */
  isGuest: boolean;
}

/**
 * Buduje submenu projektu: kolejność, ikony, adresy i warunki widoczności w jednym miejscu.
 *
 * Wcześniej każda z piętnastu pozycji była osobnym, ręcznie powielonym blokiem JSX (`NavLink` +
 * `CustomTooltip` + własny warunek) — dodanie kategorii oznaczałoby przepisanie tego samego kodu
 * jeszcze raz, tyle że zagnieżdżonego. Jako dane grupowanie jest jedną pętlą, a warunki widoczności
 * dają się przeczytać obok siebie.
 *
 * Grupa bez ani jednej widocznej pozycji nie wchodzi do wyniku — inaczej konto bez addonów
 * dostawałoby nagłówek, który po rozwinięciu jest pusty.
 */
export const buildProjectMenu = (ctx: ProjectMenuContext): ProjectMenuEntry[] => {
  const { project, addons, insights } = ctx;
  const id = project.id;
  const period = project.defaultPeriod.toLowerCase();
  const isDemo = !!project.demoVersion;
  const isActive = project.status === 'ACTIVE';
  const has = (addon: string) => addons.includes(addon);

  const overview: ProjectMenuItem = {
    id: 'overview-btn',
    to: `${PROJECTS}/${id}/${period}${ALL}`,
    match: `${PROJECTS}/${id}`,
    icon: 'ico--overview',
    labelKey: 'overview',
  };

  const prompts: Array<ProjectMenuItem | false> = [
    has('PROMPT_GROUP') &&
      !isDemo && {
        id: 'prompts-btn',
        to: `${PROMPTS}/${id}`,
        match: `${PROMPTS}/${id}`,
        icon: 'ico--question',
        labelKey: 'allPrompts',
      },
    has('SUGGESTED_PROMPTS') && {
      id: 'suggested-btn',
      to: `${SUGGESTED_PROMPTS}/${id}`,
      match: `${SUGGESTED_PROMPTS}/${id}`,
      icon: 'ico--star',
      labelKey: 'suggestedPrompts',
    },
    !isDemo &&
      ctx.promptAnalyst.canAccess && {
        id: 'prompt-analyst-btn',
        to: `${PROMPT_ANALYST}/${id}`,
        match: `${PROMPT_ANALYST}/${id}`,
        icon: 'ico--search',
        labelKey: 'promptAnalyst.navLabel',
        badge: 'new' as const,
      },
  ];

  const contextAnalysis: ProjectMenuItem | false = has('CONTEXT_INSIGHTS') &&
    !isDemo &&
    isActive && {
      id: 'context-analysis-btn',
      to: `${CONTEXT_ANALYSIS}/${id}/${period}${ALL}`,
      match: `${CONTEXT_ANALYSIS}/${id}`,
      icon: 'ico--zap',
      labelKey: 'contextAnalysis.header',
    };

  const sources: ProjectMenuItem | false = has('SOURCES_STATS') && {
    id: 'sources-btn',
    to: `${SOURCES}/${id}/${period}${ALL}`,
    match: `${SOURCES}/${id}`,
    icon: 'ico--sources',
    labelKey: 'sources',
  };

  const insightsItem: ProjectMenuItem | false = !isDemo &&
    isActive &&
    insights.canAccess && {
      id: 'insights-btn',
      to: `${INSIGHTS}/${id}/${period}${ALL}`,
      match: `${INSIGHTS}/${id}`,
      icon: 'ico--insights',
      labelKey: 'insightPage.header',
      ...(insights.isDevOnly ? { badge: 'dev' as const } : {}),
    };

  const standalone: Array<ProjectMenuItem | false> = [
    !isDemo &&
      has('KEYWORDS_PAGE') && {
        id: 'keywords-btn',
        to: `${KEYWORDS}/${id}`,
        match: `${KEYWORDS}/${id}`,
        icon: 'ico--globe',
        labelKey: 'brands',
      },
    sources,
    contextAnalysis,
    !isDemo &&
      has('AI_VISIBILITY_REPORT') && {
        id: 'ai-visibility-report-btn',
        to: `${AI_VISIBILITY_REPORT}/${id}`,
        match: `${AI_VISIBILITY_REPORT}/${id}`,
        icon: 'ico--ai-visibility-report',
        labelKey: 'aiVisibilityReport.navLabel',
        // The addon is granted only to internal accounts for now (not sold) — mark the tab so nobody takes
        // it for a client-facing feature. Drop the badge when the module goes on sale.
        badge: 'dev' as const,
      },
    !isDemo &&
      ctx.showVisibilityReport && {
        id: 'visibility-report-btn',
        to: `${REPORTS}/${id}`,
        match: `${REPORTS}/${id}`,
        icon: 'ico--report',
        labelKey: 'visibilityReport.navLabel',
        badge: ctx.isVisibilityReportDevOnly ? ('dev' as const) : ('new' as const),
      },
    !isDemo && {
      id: 'geo-checklist-btn',
      to: `${GEO_CHECKLIST}/${id}/topics-prompts`,
      match: `${GEO_CHECKLIST}/${id}`,
      icon: 'ico--clipboard',
      labelKey: 'checklists',
    },
    // Z addonem `KEYWORDS_PAGE` tagi nie grają — marki łączy się na stronie Brands.
    has('TAGS') &&
      !has('KEYWORDS_PAGE') &&
      !isDemo && {
        id: 'tags-btn',
        to: `${TAGS}/${id}`,
        match: `${TAGS}/${id}`,
        icon: 'ico--bookmark',
        labelKey: 'tags',
      },
    has('AI_SIGNALS') && {
      id: 'ai-signals-btn',
      to: `${AI_SIGNALS}/${id}`,
      match: `${AI_SIGNALS}/${id}`,
      icon: 'ico--activity',
      labelKey: 'aiSignals.navLabel',
    },
  ];

  const botAccess: ProjectMenuItem | false = !isDemo && {
    id: 'bot-access-btn',
    to: `${BOT_ACCESS}/${id}`,
    match: `${BOT_ACCESS}/${id}`,
    icon: 'ico--crawl',
    labelKey: 'botAccess.navLabel',
  };

  const bots: Array<ProjectMenuItem | false> = [
    !isDemo && {
      id: 'bots-activity-btn',
      to: ctx.botsActivityHasAnyDataSource ? BOTS_ACTIVITY_ANALYSIS(id) : `${BOTS_ACTIVITY}/${id}`,
      match: `${BOTS_ACTIVITY}/${id}`,
      icon: 'ico--bot',
      labelKey: 'botsActivity.navLabel',
    },
    botAccess,
  ];

  const ads: ProjectMenuItem | false = !isDemo &&
    ctx.hasAdsAddon && {
      id: 'ads-btn',
      to: `${ADS}/${id}/${period}${ALL}`,
      match: `${ADS}/${id}`,
      icon: 'ico--shopping-cart',
      labelKey: 'ads.navLabel',
    };

  const visible = (items: Array<ProjectMenuItem | false>): ProjectMenuItem[] =>
    items.filter((item): item is ProjectMenuItem => !!item);

  const group = (
    key: string,
    icon: string,
    items: Array<ProjectMenuItem | false>
  ): ProjectMenuEntry[] => {
    const shown = visible(items);
    return shown.length
      ? [{ kind: 'group', group: { key, labelKey: `menuGroups.${key}`, icon, items: shown } }]
      : [];
  };

  // Gość ogląda projekt, a nie pracuje w nim: Overview, Sources, analiza kontekstu, wyniki płatne
  // i dostęp botów — same odczyty o TYM projekcie. Pozostałe zakładki albo służą do zmieniania
  // projektu, albo pokazują kuchnię konta (tagi, aktywność botów, checklisty), której klient
  // agencji widzieć nie ma. To sama widoczność — dane trzyma bramka `allowGuest` na backendzie, a
  // każda pozycja zachowuje swoje własne warunki (addon, projekt niedemowy, aktywny), więc gość
  // nigdy nie zobaczy zakładki, której nie ma właściciel konta.
  if (ctx.isGuest) {
    const guestItems = [overview, sources, contextAnalysis, ads, botAccess];
    return visible(guestItems).map((item) => ({ kind: 'item' as const, item }));
  }

  // Overview and Insights form the "Performance" category — but only while Insights is actually
  // shown. Without it the group would hold a single entry, so Overview stays flat as before.
  const performance: ProjectMenuEntry[] = insightsItem
    ? group('performance', 'ico--overview', [overview, insightsItem])
    : [{ kind: 'item', item: overview }];

  return [
    ...performance,
    ...group('prompts', 'ico--question', prompts),
    ...visible(standalone).map((item) => ({ kind: 'item' as const, item })),
    ...group('bots', 'ico--bot', bots),
    ...(ads ? [{ kind: 'item' as const, item: ads }] : []),
  ];
};

/** Klucz grupy, w której leży bieżąca strona — `null`, gdy jesteśmy na pozycji spoza grup. */
export const findActiveMenuGroup = (menu: ProjectMenuEntry[], pathname: string): string | null => {
  for (const entry of menu) {
    if (entry.kind === 'group' && entry.group.items.some((item) => pathname.startsWith(item.match))) {
      return entry.group.key;
    }
  }
  return null;
};
